import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import MediaDisplay from '../../components/MediaDisplay';
import VideoPlayer from '../../components/VideoPlayer';
import { isVideoUrl } from '../../utils/mediaUtils';
import { SEARCH_TUTORS_PATH } from '../../utils/searchPaths';
import {
  buildTutorSearchPath,
  defaultTutorSearchPath,
  resolveLegacyTutorSearchRedirect,
  type TutorSearchMode,
} from '../../utils/tutorSearchMode';
import { TutorSearchResult } from '../../models/Tutor';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import TutorBookingStepperDialog from '../../components/TutorBookingStepperDialog';
import { tutorSkillLabel } from '../../utils/tutorSkillI18n';
import { resolveLanguageLabel } from '../../utils/languageOptions';
import TutorSessionDiscoveryMeta from '../../components/tutor/TutorSessionDiscoveryMeta';
import TutorSchoolAffiliation from '../../components/tutor/TutorSchoolAffiliation';
import TutorWorkingHoursSummary from '../../components/tutor/TutorWorkingHoursSummary';
import { hasAnyTutorWorkingHours } from '../../utils/tutorWorkingHours';
import TutorLessonOptionCard from '../../components/tutor/TutorLessonOptionCard';
import TutorReviewsSection from '../../components/tutor/TutorReviewsSection';
import { trackStartBooking, trackViewProvider } from '../../utils/analytics';
import { resolveTutorById } from '../../utils/resolveTutorById';
import { tutorApiClient } from '../../tutorApiClient';
import type { TutorReviewWithUsername } from '../../models/TutorReview';
import PassOfferList from '../../components/passes/PassOfferList';
import PassPurchaseConfirmDialog from '../../components/passes/PassPurchaseConfirmDialog';
import PassCheckoutResumeErrorAlert from '../../components/passes/PassCheckoutResumeErrorAlert';
import { PassOfferSummary } from '../../models/Pass';
import { isPassPurchaseAvailable } from '../../utils/passPurchaseAvailability';
import { resolveTutorBookingPaymentMethods } from '../../utils/tutorBookingPaymentMethods';
import {
  resolvePassOfferSessionType,
  resolvePassOfferTargetNames,
} from '../../utils/passFormatting';
import {
  readTutorBookingResumeFromSearchParams,
  stripTutorBookingResumeSearchParams,
} from '../../utils/bookingLoginResume';

type TutorListingLocationState = {
  tutor?: TutorSearchResult;
  mode?: TutorSearchMode;
  city?: string;
  /** @deprecated legacy navigation state — use `city` */
  location?: string;
  searchBackPath?: string;
  /** @deprecated legacy navigation state — use `searchBackPath` */
  searchQuery?: string;
};

const TutorListingPage: React.FC = () => {
  const { tutorId } = useParams<{ tutorId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  const locationState = (location.state as TutorListingLocationState | null) ?? null;
  const [tutor, setTutor] = useState<TutorSearchResult | null>(
    locationState?.tutor && locationState.tutor.tutorId === tutorId ? locationState.tutor : null
  );
  const [loading, setLoading] = useState(!tutor);
  const [error, setError] = useState<string | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [initialBookingLessonOptionId, setInitialBookingLessonOptionId] = useState<string | null>(null);
  const [initialBookingStartTime, setInitialBookingStartTime] = useState<string | null>(null);
  const [initialBookingStepKey, setInitialBookingStepKey] = useState<'paymentOptions' | null>(null);
  const [reviews, setReviews] = useState<TutorReviewWithUsername[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedPassOffer, setSelectedPassOffer] = useState<PassOfferSummary | null>(null);
  const [passDialogOpen, setPassDialogOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  const portfolioUrls = useMemo(
    () => (tutor?.imageUrls ?? []).filter((url) => typeof url === 'string' && url.trim()),
    [tutor?.imageUrls]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'auto' });
  }, [tutorId]);

  useEffect(() => {
    if (!tutorId) {
      setError(t('tutorSearch.loadError', 'Failed to load tutors'));
      setLoading(false);
      return;
    }

    // Optimistic paint from navigation state, but always refetch so portfolio
    // `imageUrls` (and other listing fields) are not stale/missing.
    const seeded =
      locationState?.tutor?.tutorId === tutorId ? locationState.tutor : null;
    if (seeded) {
      setTutor(seeded);
      setLoading(false);
    }

    const loadTutor = async () => {
      if (!seeded) {
        setLoading(true);
      }
      setError(null);
      try {
        const found = await resolveTutorById(tutorId);
        if (!found) {
          setError(
            t(
              'tutorSearch.notFound',
              'This tutor is unavailable or not publicly listed.',
            ),
          );
        }
        setTutor(found);
        if (found) {
          trackViewProvider({
            provider_type: 'tutor',
            provider_id: found.tutorId,
            provider_name: found.name,
          });
        }
      } catch (_e) {
        setError(t('tutorSearch.loadError', 'Failed to load tutors'));
      } finally {
        setLoading(false);
      }
    };

    void loadTutor();
  }, [locationState, tutorId, t]);

  const startTutorBooking = (lessonOptionId: string | null = null) => {
    if (!tutor) return;
    const option = lessonOptionId
      ? tutor.lessonOptions?.find((o) => o.id === lessonOptionId)
      : tutor.lessonOptions?.[0];
    trackStartBooking({
      provider_type: 'tutor',
      provider_id: tutor.tutorId,
      booking_value: option?.price,
      currency: option?.lessonPriceCurrency ?? 'PLN',
    });
    setInitialBookingLessonOptionId(lessonOptionId);
    setBookingOpen(true);
  };

  useEffect(() => {
    if (!tutor) return;
    const resume = readTutorBookingResumeFromSearchParams(searchParams);
    if (!resume.open) return;

    setInitialBookingLessonOptionId(resume.lessonOptionId);
    setInitialBookingStartTime(resume.startTime);
    setInitialBookingStepKey('paymentOptions');
    trackStartBooking({
      provider_type: 'tutor',
      provider_id: tutor.tutorId,
    });
    setBookingOpen(true);
    setSearchParams(stripTutorBookingResumeSearchParams(searchParams), { replace: true });
  }, [tutor, searchParams, setSearchParams]);

  useEffect(() => {
    if (!tutorId) {
      setReviews([]);
      return;
    }

    let cancelled = false;

    const loadReviews = async () => {
      setReviewsLoading(true);
      try {
        const data = await tutorApiClient.getTutorReviews(tutorId);
        if (!cancelled) {
          setReviews(data);
        }
      } catch (_e) {
        if (!cancelled) {
          setReviews([]);
        }
      } finally {
        if (!cancelled) {
          setReviewsLoading(false);
        }
      }
    };

    void loadReviews();

    return () => {
      cancelled = true;
    };
  }, [tutorId]);

  const tutorPaymentMethods = resolveTutorBookingPaymentMethods(tutor?.supportedPaymentMethods);
  const canPurchasePasses = isPassPurchaseAvailable(tutorPaymentMethods);
  const passOffers = useMemo(
    () => (canPurchasePasses ? (tutor?.passOffers ?? []) : []),
    [canPurchasePasses, tutor?.passOffers],
  );

  const handleBuyPassOffer = (offer: PassOfferSummary) => {
    setSelectedPassOffer(offer);
    setPassDialogOpen(true);
  };

  const formatMoney = (amount: number, currency?: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const lessonOptionPriceLabel = useMemo(() => {
    if (!tutor) return '—';
    const cheapest = (tutor.lessonOptions ?? []).reduce<{ price: number; currency: string } | null>(
      (best, option) =>
        best == null || option.price < best.price
          ? { price: option.price, currency: option.lessonPriceCurrency ?? 'PLN' }
          : best,
      null
    );
    if (cheapest == null) return '—';
    return t('tutorSearch.fromLessonPrice', 'From {{price}} / lesson', {
      price: formatMoney(cheapest.price, cheapest.currency),
    });
  }, [t, tutor, i18n.language]);

  const skillChips = useMemo(() => {
    if (!tutor?.skills?.length) return [];
    return tutor.skills.map((skill) => tutorSkillLabel(t, skill));
  }, [t, tutor?.skills]);

  const reviewCount = tutor?.reviewCount ?? 0;
  const avgReviewRating = tutor?.avgReviewRating ?? 0;

  const handleBackToSearch = useCallback(() => {
    if (locationState?.searchBackPath) {
      navigate(locationState.searchBackPath);
      return;
    }
    if (locationState?.searchQuery) {
      const raw = locationState.searchQuery;
      const search = raw.startsWith('?') ? raw.slice(1) : raw;
      navigate(resolveLegacyTutorSearchRedirect(SEARCH_TUTORS_PATH, search));
      return;
    }
    if (locationState?.mode) {
      navigate(
        buildTutorSearchPath({
          mode: locationState.mode,
          city: locationState.city ?? locationState.location,
        })
      );
      return;
    }
    const referrer = document.referrer;
    if (referrer && referrer.startsWith(window.location.origin)) {
      navigate(-1);
      return;
    }
    navigate(defaultTutorSearchPath());
  }, [locationState, navigate]);

  return (
    <>
      <SEO title={t('navigation.myProfile', 'Tutor profile')} noindex={true} />
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 1.5, sm: 2.5, lg: 4 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Chip
          label={`← ${t('tutorSearch.backToResults', 'Back to search results')}`}
          onClick={handleBackToSearch}
          sx={{
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'transparent',
            },
          }}
        />
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2.5, lg: 3 }, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error || !tutor ? (
            <Alert severity="error">{error || t('tutorSearch.loadError', 'Failed to load tutors')}</Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, lg: 3 },
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
                alignItems: 'start',
              }}
            >
              <Stack spacing={2.5} minWidth={0}>
                <Box
                  sx={{
                    p: { xs: 2, sm: 3 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 2, md: 3 }}
                    alignItems={{ xs: 'center', md: 'flex-start' }}
                  >
                    <Avatar
                      src={tutor.profileImageUrl || undefined}
                      alt=""
                      variant="rounded"
                      sx={{
                        width: { xs: '100%', sm: 220, md: 240 },
                        maxWidth: { xs: 280, sm: 220, md: 240 },
                        height: { xs: 220, sm: 220, md: 240 },
                        borderRadius: 2,
                        bgcolor: '#4AA7C6',
                        color: '#ffffff',
                        fontSize: { xs: '3rem', md: '3.5rem' },
                      }}
                    >
                      {tutorAvatarInitials(tutor.name)}
                    </Avatar>
                    <Stack spacing={1.25} flex={1} minWidth={0} sx={{ width: '100%' }}>
                      <Box minWidth={0}>
                        <Typography variant="h4" sx={{ lineHeight: 1.15 }}>
                          {tutor.name}
                        </Typography>
                        <TutorSchoolAffiliation
                          organizationType={tutor.organizationType}
                          spaceId={tutor.spaceId}
                          spaceName={tutor.spaceName}
                        />
                        {(tutor.headline ?? '').trim() ? (
                          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.75 }}>
                            {(tutor.headline ?? '').trim()}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Stack direction="row" spacing={0} sx={{ display: 'inline-flex' }}>
                          {reviewCount > 0 ? (
                            <StarRoundedIcon sx={{ color: '#F8C14A', fontSize: 20 }} />
                          ) : (
                            Array.from({ length: 5 }, (_, i) => (
                              <StarBorderRoundedIcon key={i} sx={{ color: 'action.disabled', fontSize: 20 }} />
                            ))
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {avgReviewRating.toFixed(2)} ({reviewCount}{' '}
                          {t('reviews.reviews', 'reviews')})
                        </Typography>
                      </Stack>
                      <TutorSessionDiscoveryMeta
                        supportedSessionTypes={tutor.supportedSessionTypes}
                        studentLessonCities={tutor.studentLessonCities}
                      />
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                        <Chip
                          icon={<EventAvailableRoundedIcon />}
                          label={t('tutorSearch.statsConfirmedBookings', 'Bookings: {{count}}', {
                            count: tutor.bookingCount ?? 0,
                          })}
                          variant="outlined"
                        />
                        <Chip
                          icon={<GroupsRoundedIcon />}
                          label={t('tutorSearch.statsStudents', 'Students: {{count}}', {
                            count: tutor.studentCount ?? 0,
                          })}
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>

                {(tutor.overview ?? '').trim() ? (
                  <Box
                    sx={{
                      p: { xs: 2, sm: 3 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('tutor.profileCompletion.overview', 'Overview')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {(tutor.overview ?? '').trim()}
                    </Typography>
                  </Box>
                ) : null}

                {portfolioUrls.length > 0 ? (
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      {t('tutor.profileCompletion.portfolioGallery', 'Portfolio gallery')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.25,
                        overflowX: 'auto',
                        pb: 0.5,
                        mx: { xs: -0.5, sm: 0 },
                        px: { xs: 0.5, sm: 0 },
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      {portfolioUrls.map((url, index) => (
                        <Box
                          key={`${url}-${index}`}
                          sx={{
                            position: 'relative',
                            flex: '0 0 auto',
                            width: { xs: 140, sm: 160 },
                            height: { xs: 105, sm: 120 },
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.900',
                          }}
                        >
                          <MediaDisplay
                            src={url}
                            alt=""
                            onClick={() => setGalleryIndex(index)}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              cursor: 'pointer',
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                    <Dialog
                      open={galleryIndex !== null}
                      onClose={() => setGalleryIndex(null)}
                      maxWidth="lg"
                      fullWidth
                    >
                      <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
                        {galleryIndex !== null && portfolioUrls[galleryIndex] ? (
                          <>
                            {isVideoUrl(portfolioUrls[galleryIndex]) ? (
                              <VideoPlayer
                                src={portfolioUrls[galleryIndex]}
                                alt={tutor.name}
                                sx={{ width: '100%', height: 'auto', maxHeight: '90vh' }}
                              />
                            ) : (
                              <MediaDisplay
                                src={portfolioUrls[galleryIndex]}
                                alt={tutor.name}
                                showPlayButton={false}
                                sx={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: '90vh',
                                  objectFit: 'contain',
                                }}
                              />
                            )}
                            {portfolioUrls.length > 1 ? (
                              <>
                                <IconButton
                                  onClick={() =>
                                    setGalleryIndex(
                                      (galleryIndex - 1 + portfolioUrls.length) % portfolioUrls.length
                                    )
                                  }
                                  aria-label={t('common.previous', 'Previous')}
                                  sx={{
                                    position: 'absolute',
                                    left: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                                  }}
                                >
                                  <KeyboardArrowLeft />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    setGalleryIndex((galleryIndex + 1) % portfolioUrls.length)
                                  }
                                  aria-label={t('common.next', 'Next')}
                                  sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                                  }}
                                >
                                  <KeyboardArrowRight />
                                </IconButton>
                              </>
                            ) : null}
                          </>
                        ) : null}
                      </DialogContent>
                    </Dialog>
                  </Box>
                ) : null}

                <TutorReviewsSection
                  reviews={reviews}
                  averageRating={avgReviewRating}
                  reviewCount={reviewCount}
                  loading={reviewsLoading}
                />

                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={0.55} alignItems="center">
                      <MusicNoteRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="overline" color="text.secondary">
                        {t('tutorSearch.skills', 'Skills')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      {skillChips.length > 0 ? (
                        skillChips.map((label) => <Chip key={label} size="small" label={label} />)
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={0.55} alignItems="center">
                      <TranslateRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="overline" color="text.secondary">
                        {t('tutorSearch.languages', 'Languages')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      {(tutor.languages ?? []).length > 0 ? (
                        tutor.languages.map((code) => (
                          <Chip
                            key={`${tutor.tutorId}-lang-${code}`}
                            size="small"
                            label={resolveLanguageLabel(code, i18n.language)}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>

                {tutor.timezone || hasAnyTutorWorkingHours(tutor.workingHours) ? (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <TutorWorkingHoursSummary
                      workingHours={tutor.workingHours}
                      timezone={tutor.timezone}
                    />
                  </Box>
                ) : null}
              </Stack>

              <Box
                sx={{
                  position: { lg: 'sticky' },
                  top: { lg: 24 },
                  p: { xs: 2, sm: 2.5 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.shadows[2],
                }}
              >
                <Stack spacing={2}>
                  <Typography variant="h5" sx={{ lineHeight: 1.15 }}>
                    {lessonOptionPriceLabel}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => {
                      startTutorBooking(null);
                    }}
                  >
                    {t('tutorSearch.bookTutor', 'Book Tutor')}
                  </Button>

                  <PassCheckoutResumeErrorAlert />
                  {canPurchasePasses && passOffers.length > 0 && (
                    <PassOfferList
                      offers={passOffers}
                      onBuy={handleBuyPassOffer}
                    />
                  )}

                  <Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                      <MenuBookRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="overline" color="text.secondary">
                        {t('tutorBooking.lessonOptions', 'Lesson options')}
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      {(tutor.lessonOptions ?? []).length > 0 ? (
                        tutor.lessonOptions.map((option) => (
                          <TutorLessonOptionCard
                            key={option.id ?? `${option.name}-${option.durationMinutes}-${option.price}`}
                            option={option}
                            onClick={() => {
                              startTutorBooking(option.id ?? null);
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <TutorBookingStepperDialog
        open={bookingOpen}
        tutor={tutor}
        initialLessonOptionId={initialBookingLessonOptionId}
        initialStartTime={initialBookingStartTime}
        initialStepKey={initialBookingStepKey}
        onClose={() => {
          setBookingOpen(false);
          setInitialBookingStartTime(null);
          setInitialBookingStepKey(null);
        }}
      />

      <PassPurchaseConfirmDialog
        open={passDialogOpen}
        offer={selectedPassOffer}
        supportedPaymentMethods={tutorPaymentMethods}
        regulation={selectedPassOffer?.regulation ?? tutor?.regulation}
        termsName={tutor?.organizationName ?? tutor?.name}
        targetNames={
          selectedPassOffer ? resolvePassOfferTargetNames(selectedPassOffer) : null
        }
        sessionType={
          selectedPassOffer ? resolvePassOfferSessionType(selectedPassOffer) : null
        }
        onClose={() => setPassDialogOpen(false)}
      />
    </>
  );
};

export default TutorListingPage;
